/**
 * The account controls: the header button and the one utility popup behind it.
 *
 * This site reads the account; it does not manage it. Signing in stays here
 * because the conversation lives in the browser — bouncing to the root site at
 * the moment a reader hits the daily cap would throw away what they were in the
 * middle of. Everything else about an account (password, export, deletion)
 * belongs to the root site, once, rather than to each corpus site separately.
 * The one exception is a forced password change: an operator-issued password
 * must stop working before the reader does anything, wherever they landed.
 *
 * The design's interaction ladder is hover → click → expand, and it reserves
 * popups for utilities — sign-in, the cap notice, upload. The output window is
 * for *the text*, and putting a password field there would blur the one
 * distinction the whole layout rests on.
 *
 * The sign-in dialog, and its links *out* to the root site to create an account
 * or reset a password, live in `signInDialog.tsx`; the parts every dialog is
 * built from live in `dialog.tsx`.
 */

import type { JSX } from 'react'

import { useEffect, useState } from 'react'
import type { Account } from './account'
import { AccountError, changePassword, signOut } from './account'
import type { Card } from './cards'
import { Dialog, Field } from './dialog'
import { t } from './i18n'
import { ACCOUNT_URL } from './origins'
import { SignInDialog } from './signInDialog'

type Panel = 'signIn' | 'mustChange' | null

/** The workspace, as a menu. Each entry pushes one card onto the output stack. */
const WORKSPACE: readonly { card: Card; label: Parameters<typeof t>[0] }[] = [
  { card: { kind: 'findings' }, label: 'account.myFindings' },
  { card: { kind: 'themes' }, label: 'themes.title' },
  { card: { kind: 'threads' }, label: 'threads.title' },
  { card: { kind: 'packs' }, label: 'packs.title' },
]

interface AccountControlProps {
  account: Account | null
  /** Findings whose citations need a look — a badge, never an interruption. */
  queued: number
  onChange: (account: Account | null) => void
  onOpen: (card: Card) => void
}

export function AccountControl(props: AccountControlProps): JSX.Element {
  const { account, queued, onChange, onOpen } = props
  const [panel, setPanel] = useState<Panel>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  useForcedPasswordChange(account, () => setPanel('mustChange'))

  if (!account) {
    return (
      <SignedOutControl
        showingDialog={panel === 'signIn'}
        onOpenDialog={() => setPanel('signIn')}
        onCloseDialog={() => setPanel(null)}
        onSignedIn={(signedIn) => {
          onChange(signedIn)
          // Straight into the forced change if the issued password is still in play —
          // an operator-issued credential is one somebody else has seen.
          setPanel(signedIn.must_change_password ? 'mustChange' : null)
        }}
      />
    )
  }

  return (
    <>
      <MenuTrigger
        label={account.email}
        queued={queued}
        onToggle={() => setMenuOpen((open) => !open)}
      />
      {menuOpen && (
        <AccountMenu
          account={account}
          queued={queued}
          onOpen={(card) => {
            setMenuOpen(false)
            onOpen(card)
          }}
          onSignOut={async () => {
            setMenuOpen(false)
            await signOut()
            onChange(null)
          }}
        />
      )}

      {panel === 'mustChange' && (
        <PasswordDialog
          forced
          onClose={() => setPanel(null)}
          onChanged={(updated) => {
            onChange(updated)
            setPanel(null)
          }}
        />
      )}
    </>
  )
}

/**
 * Raise the change-password panel the moment an account arrives still carrying the flag.
 *
 * Not dismissible, and that is the point: an operator-issued password is a credential
 * somebody else has seen, so it has to stop working before the reader does anything else.
 */
function useForcedPasswordChange(account: Account | null, onRequired: () => void): void {
  useEffect(() => {
    if (account?.must_change_password) onRequired()
    // Keyed on the flag alone: the callback is a fresh closure every render, and the panel
    // should open when the flag turns true rather than on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.must_change_password])
}

/**
 * What opens the account menu: the address, and the review count if there is one.
 *
 * The badge sits on the trigger rather than in the menu because it has to be visible
 * without opening anything — it is a standing invitation to tidy up, and one nobody sees
 * is not an invitation.
 */
function MenuTrigger({
  label,
  queued,
  onToggle,
}: {
  label: string
  queued: number
  onToggle: () => void
}): JSX.Element {
  return (
    <button type="button" className="link" onClick={onToggle}>
      {label}
      {queued > 0 && (
        <span className="badge badge--problem" title={t('review.title')}>
          {queued}
        </span>
      )}
    </button>
  )
}

/** Nobody signed in: the one control, and the dialog it opens. */
function SignedOutControl({
  showingDialog,
  onOpenDialog,
  onCloseDialog,
  onSignedIn,
}: {
  showingDialog: boolean
  onOpenDialog: () => void
  onCloseDialog: () => void
  onSignedIn: (account: Account) => void
}): JSX.Element {
  return (
    <>
      <button type="button" className="link" onClick={onOpenDialog}>
        {t('account.signIn')}
      </button>
      {showingDialog && <SignInDialog onClose={onCloseDialog} onSignedIn={onSignedIn} />}
    </>
  )
}

/**
 * The signed-in menu: what this account is, and where the workspace lives.
 *
 * Facts, not controls. A corpus site reads the account; everything that changes it
 * account-wide — password, export, deletion, publishing identity — lives at the root, and
 * "Manage account" is a link there rather than a dialog here.
 *
 * Closing the menu is the caller's business, which is why every action here reports out
 * rather than reaching for the parent's state.
 */
function AccountMenu({
  account,
  queued,
  onOpen,
  onSignOut,
}: {
  account: Account
  queued: number
  onOpen: (card: Card) => void
  onSignOut: () => void
}): JSX.Element {
  return (
    <nav className="menu">
      <span className="menu__fact">
        {/* A null limit is an uncapped account, not a missing number. */}
        {account.questions.limit === null
          ? t('account.questionsUnlimited')
          : t('account.questionsLeft', {
              remaining: account.questions.remaining ?? 0,
              limit: account.questions.limit,
            })}
      </span>
      <span className="menu__fact">
        {account.has_consented_to_findings
          ? t('account.storingOn')
          : t('account.storingOff')}
      </span>
      {WORKSPACE.map((entry) => (
        <button
          key={entry.label}
          type="button"
          className="link"
          onClick={() => onOpen(entry.card)}
        >
          {t(entry.label)}
        </button>
      ))}
      {queued > 0 && (
        <button type="button" className="link" onClick={() => onOpen({ kind: 'review' })}>
          {t('review.menu', { count: queued })}
        </button>
      )}
      <a className="link" href={ACCOUNT_URL}>
        {t('account.manage')}
      </a>
      <button type="button" className="link" onClick={onSignOut}>
        {t('account.signOut')}
      </button>
    </nav>
  )
}

function PasswordDialog({
  forced,
  onClose,
  onChanged,
}: {
  forced?: boolean
  onClose: () => void
  onChanged: (account: Account) => void
}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    try {
      onChanged(await changePassword(current, next))
    } catch (error) {
      setFailure(error instanceof AccountError ? error.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      title={t('account.changePassword')}
      onClose={forced ? undefined : onClose}
    >
      <form onSubmit={submit} className="form">
        {forced && <p>{t('account.mustChange')}</p>}
        <Field
          label={t('account.currentPassword')}
          type="password"
          value={current}
          autoComplete="current-password"
          onChange={setCurrent}
        />
        <Field
          label={t('account.newPassword')}
          type="password"
          value={next}
          autoComplete="new-password"
          onChange={setNext}
        />
        {failure && (
          <p className="failure" role="alert">
            {failure}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {t('account.changePassword')}
        </button>
      </form>
    </Dialog>
  )
}

/** The one-time Article 9 dialog, shown before the first finding is ever stored. */
export function ConsentDialog({
  onAgree,
  onDecline,
}: {
  onAgree: () => void
  onDecline: () => void
}): JSX.Element {
  return (
    <Dialog title={t('consent.title')} onClose={onDecline}>
      <div className="form">
        <p>{t('consent.body')}</p>
        <p className="muted">{t('consent.alternative')}</p>
        <button type="button" onClick={onAgree}>
          {t('consent.agree')}
        </button>
        <button type="button" className="link" onClick={onDecline}>
          {t('consent.decline')}
        </button>
      </div>
    </Dialog>
  )
}
